import type { CalendarContext } from "@/types/threadEvents.ts";
import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

/**
 * Capability flags: which scheduling-flow fields an enrichment is supplying
 * out-of-band at send time. Consumers (e.g. the guided flow) read these to
 * decide which user-facing steps to skip. Each enrichment declares what it
 * provides; the guided flow never needs to know *which* enrichment did it or
 * how.
 */
export interface EnrichmentProvides {
  /** The "when?" (time/date) step is satisfied — don't ask the user for a time. */
  when?: boolean;
  /** The "how long?" (duration) step is satisfied. */
  duration?: boolean;
}

/**
 * What an enrichment's `buildMessage` returns. An enrichment may supply
 * either an inline `agentContent` blob (legacy, used for arbitrary
 * non-calendar enrichments) or a structured `calendarContext` the server
 * expands into agentContent by fetching events itself (calendar enrichments,
 * post-BIG-4941). At most one should be set per enrichment.
 */
export interface EnrichmentBuildResult {
  text: string;
  agentContent?: string;
  calendarContext?: CalendarContext;
}

/**
 * Where an enrichment is allowed to surface. The enrichment context is a single
 * app-wide bag, but not every enrichment belongs in every omnibox:
 *
 * - `"selection"` (default) — tied to an explicit user selection (a drawn time
 *   range, a clicked event). It follows the selection into whatever omnibox is
 *   in play, including a thread's composer, so it shows everywhere.
 * - `"page"` — tied to a page's ambient state (e.g. the calendar's current view
 *   week). It belongs only to that page's own omnibox and must NOT leak into
 *   unrelated composers that happen to be mounted on the same page (a thread
 *   sidebar, an artifact "Ask a follow up" row). Only omniboxes that opt in via
 *   `hostsPageEnrichments` render these.
 */
export type EnrichmentScope = "page" | "selection";

export interface OmniboxEnrichment {
  id: string;
  /** See {@link EnrichmentScope}. Defaults to `"selection"` when unset. */
  scope?: EnrichmentScope;
  /** Rendered above the textarea (chips, context pills, etc.) */
  renderContent: () => ReactNode;
  /** Transform message before send. Return undefined to skip. */
  buildMessage?: (text: string) => EnrichmentBuildResult | undefined;
  /** Handle Escape. Return true if consumed. */
  onEscape?: () => boolean;
  /**
   * Which scheduling-flow fields this enrichment is supplying out-of-band.
   * Read by `useGuidedFlowIntegration` to skip steps whose answer is implied
   * by the enrichment; generic (no enrichment-specific knowledge required).
   */
  provides?: EnrichmentProvides;
}

/**
 * Filter enrichments down to those an omnibox should surface. Selection-scoped
 * enrichments (the default) always pass; page-scoped ones pass only when the
 * omnibox opts in via `hostsPageEnrichments`. Pure so it can be unit-tested
 * without rendering an omnibox.
 */
export function filterVisibleEnrichments(
  enrichments: OmniboxEnrichment[],
  hostsPageEnrichments: boolean,
): OmniboxEnrichment[] {
  return enrichments.filter(
    (e) => (e.scope ?? "selection") === "selection" || hostsPageEnrichments,
  );
}

interface OmniboxEnrichmentContextValue {
  enrichments: OmniboxEnrichment[];
  register: (enrichment: OmniboxEnrichment) => void;
  unregister: (id: string) => void;
}

const defaultValue: OmniboxEnrichmentContextValue = {
  enrichments: [],
  register: () => {},
  unregister: () => {},
};

const OmniboxEnrichmentContext = createContext<OmniboxEnrichmentContextValue>(defaultValue);

export const useOmniboxEnrichment = () => useContext(OmniboxEnrichmentContext);

export const OmniboxEnrichmentProvider = ({ children }: { children: ReactNode }) => {
  const [enrichments, setEnrichments] = useState<OmniboxEnrichment[]>([]);

  const register = useCallback((enrichment: OmniboxEnrichment) => {
    setEnrichments((prev) => {
      const idx = prev.findIndex((e) => e.id === enrichment.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = enrichment;
        return next;
      }
      return [...prev, enrichment];
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setEnrichments((prev) => {
      const next = prev.filter((e) => e.id !== id);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  return (
    <OmniboxEnrichmentContext.Provider value={{ enrichments, register, unregister }}>
      {children}
    </OmniboxEnrichmentContext.Provider>
  );
};
